import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { object, string } from "yup";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import { forgotPassword } from "../../services/AuthServices";
import authIllustrations from "../../assets/images/auth-illustrations.png";
import "../Login/Auth.css";

const MainSection = () => {
  const [isDisable, setisDisable] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onRecaptchaChange = (token) => setRecaptchaToken(token);
  const onRecaptchaExpired = () => setRecaptchaToken(null);
  const onRecaptchaError = () => setRecaptchaError(true);

  const resetRecaptcha = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const AdminSchema = object().shape({
    email: string()
      .required("Please enter your email")
      .email("Please enter a valid email address")
      .trim(),
  });

  const submitClicked = async (data) => {
    setisDisable(true);
    let formdata = new FormData();
    formdata.append("email", values.email);

    await forgotPassword(formdata)
      .then((response) => {
        setisDisable(false);
        toast("Reset password link sent successfully to your email", { type: "success" });
        navigate("/login");
      })
      .catch((error) => {
        setisDisable(false);
        resetRecaptcha();
        if (error?.response?.status == 422)
          toast(error.response.data.error, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error?.response.data?.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", { type: "error" });
      });
  };

  const { handleSubmit, handleBlur, handleChange, values, errors, touched } =
    useFormik({
      validationSchema: AdminSchema,
      initialValues: { email: "" },
      onSubmit: async () => {
        if (!recaptchaError && !recaptchaToken) {
          toast("Please complete the reCAPTCHA verification.", { type: "error" });
          return;
        }
        submitClicked(values);
      },
      onReset: () => {},
    });

  return (
    <div className="dp-auth-container">
      <div className="dp-auth-card" style={{ maxWidth: '900px' }}>
        {/* Left Side - Illustration */}
        <div className="dp-auth-left">
          <div
            className="dp-auth-illustration-wrapper reset"
            style={{ backgroundImage: `url(${authIllustrations})` }}
          ></div>
          <div className="dp-auth-brand">
            <h2>Forgot Password?</h2>
            <p>Don't worry! It happens. Enter your email and we'll send you a reset link.</p>
            <div className="dp-auth-features">
              <div className="dp-auth-feature">
                <i className="fas fa-envelope"></i>
                <span>Check your inbox</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-link"></i>
                <span>Click the reset link</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-key"></i>
                <span>Create new password</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="dp-auth-right">
          <div className="dp-auth-header">
            <h1>Reset Password</h1>
            <p>Enter your email to receive a password reset link</p>
          </div>

          <form className="dp-auth-form" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="dp-form-group">
              <label className="dp-form-label">Email Address</label>
              <div className="dp-form-input-wrapper">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className={`dp-form-input ${touched.email && errors.email ? 'error' : ''}`}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <i className="fal fa-envelope dp-form-icon"></i>
              </div>
              {touched.email && errors.email && (
                <span className="dp-form-error">{errors.email}</span>
              )}
            </div>

            {/* reCAPTCHA */}
            {!recaptchaError && (
              <div className="dp-recaptcha-wrapper">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchaChange}
                  onExpired={onRecaptchaExpired}
                  onErrored={onRecaptchaError}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="dp-auth-submit"
              disabled={isDisable}
            >
              {isDisable ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Sending Link...
                </>
              ) : (
                <>
                  <i className="far fa-paper-plane"></i>
                  Send Reset Link
                </>
              )}
            </button>

            {/* Footer */}
            <div className="dp-auth-footer">
              <p>
                Remember your password?{" "}
                <span onClick={() => navigate("/login")}>Back to Login</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MainSection;
